import { useContext } from "react";
import { getGroupAPI } from "../api/api";
import {
    addQuoteToGroupAPI,
    createQuoteAPI,
    selectQuoteCaseAPI
} from "../api/endpoints/ApiQuotes";
import { ClientProductTypes } from "../../common/interfaces/enums/ProductType";
import { IClient } from "../../common/interfaces/IClient";
import { QuoteGroupContext } from "../providers/RootContextProvider";
import { QuoteSettingContainerType } from "../../common/interfaces/settings/QuoteSettingContainerType";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { retrieveGroupMapper } from "../api/mapper/mapper";

type SetGroupSettingsFunction = (settings: QuoteSettingContainerType[]) => Promise<void>;
type SetProductSelectedFunction = (
    productType: ClientProductTypes,
    isEnabled: boolean
) => Promise<void>;

interface IUseAPIHook {
    setGroupSettings: SetGroupSettingsFunction;
    setProductSelected: SetProductSelectedFunction;
}
/**
 * This hook is to handle complex multi-step interactions with LifeDirect API and act as a simplifying interface for such behavior.
 * @returns Methods for interacting with various APIs
 */
const useAPI = (): IUseAPIHook => {
    const { selectors, actions } = useContext(QuoteGroupContext);
    const { Log } = useErrorLogger();

    const groupId = selectors.getGroupId();

    const addSettingsToGroup = async (
        setting: QuoteSettingContainerType,
        clients: IClient[]
    ): Promise<void> => {
        const mapper = retrieveGroupMapper(setting.productType);

        const caseId = await createQuoteAPI(mapper, clients, setting);

        await addQuoteToGroupAPI(groupId, caseId, true);
    };

    const setGroupSettings: SetGroupSettingsFunction = (
        settings: QuoteSettingContainerType[]
    ): Promise<void> => {
        const clients = selectors.getClients();

        const settingsUpdate = settings.map((s) => addSettingsToGroup(s, clients));

        return Promise.all(settingsUpdate)
            .then(async () => {
                // Once we have updated them all, we need to get the current state from the server
                const quoteGroup = await getGroupAPI(groupId);

                actions.populateQuoteConfigs(
                    quoteGroup.quoteGroupCases.map((s) => s.config)
                );
            })
            .catch((e) => {
                Log(e, {
                    source: "API - Multi-quote settings creation"
                });
                throw e;
            });
    };

    const setProductSelected: SetProductSelectedFunction = async (
        productType: ClientProductTypes,
        isEnabled: boolean
    ): Promise<void> => {
        const setting = selectors.getQuoteSettingByProductType(productType);

        await selectQuoteCaseAPI(groupId, setting.caseId, isEnabled).then((res) => {
            actions.setProductEnabled(productType, res.isSelected);
        });
    };

    return {
        setGroupSettings,
        setProductSelected
    };
};

export default useAPI;
